import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, Alert, Badge, Container, Row, Col, Placeholder, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import MainContainer from "../../MainContainer";
import Cards from './Cards';
import NewCard from './NewCard';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle } from "react-icons/fa";
import { BsGift, BsLightningCharge, BsRocket, BsBuilding } from "react-icons/bs";
import { headers } from '../../../helpers/Requests';
import { baseURL } from '../../../helpers/baseURL';
import axios from 'axios';
import styles from './Plan.module.css';

const PLAN_FEATURES = [
	{ key: 'users', name: 'Users', type: 'number', suffix: '' },
	{ key: 'tickets', name: 'Tickets', type: 'number', suffix: '' },
	{ key: 'departments', name: 'Departments', type: 'number', suffix: '' },
	{ key: 'incoming_email', name: 'Incoming Emails', type: 'number', suffix: '' },
	{ key: 'file_uploads', name: 'Media Storage', type: 'number', suffix: 'MB' },
	{ key: 'support', name: 'Full Support', type: 'check', suffix: '' },
	{ key: 'claritask_connect', name: 'Claritask Integration', type: 'check', suffix: '' },
];

const PLAN_ICONS = {
	Free: BsGift,
	Standard: BsLightningCharge,
	Premium: BsRocket,
	Enterprise: BsBuilding,
};

const CANCELLATION_REASONS = [
	'Too expensive',
	'Missing features I need',
	"I'm not using it enough",
	'Switching to another product',
	'Too difficult to use',
	'Technical issues or bugs',
	'Other',
];

const getPlanTier = (plan) => {
	if (!plan) return 0;
	if (plan.name === 'Enterprise') return 1000;
	return plan.amount ?? 0;
};

const getPlanActionLabel = (currentPlan, targetPlan, { needsReactivation = false } = {}) => {
	if (!targetPlan || !currentPlan) return 'Select Plan';
	if (targetPlan.name === currentPlan.name) {
		return needsReactivation ? 'Reactivate' : 'Current Plan';
	}
	return getPlanTier(targetPlan) < getPlanTier(currentPlan) ? 'Downgrade Plan' : 'Upgrade Plan';
};

const formatLimit = (value, suffix = '') => {
	if (value === -1 || value === null || value === undefined) return 'Unlimited';
	if (value >= 50000) return 'Unlimited';
	return `${Number(value).toLocaleString('en-US')}${suffix}`;
};

const isEnterprisePlan = (plan) => plan?.name === 'Enterprise' || plan?.amount === null;
const isAdditionalUserPlan = (plan) => plan?.name === 'Additional User';

/** Copy aligned with landingData, Pricing.js, upgrade modal & PlansSeeder */
const ENTERPRISE_CONTENT = {
	tagline: 'Ideal for large companies and product teams',
	description: 'Enterprise is a custom package tailored to your agreement, limits, onboarding, and support are configured to fit your organization.',
	offerings: [
		'Everything in Premium',
		'Custom onboarding & training',
		'Dedicated support',
		'Custom SLAs',
		'Multi-brand workspaces',
		'Tailored limits, storage & billing',
	],
	offeringsNote: 'Everything listed below is included — we can add or customize services per your agreement.',
	capacityNote: 'Baseline capacity below, your actual limits are set per your agreement.',
	contactUrl: 'https://kore.co/contact',
	contactEmail: 'info@claritick.com',
};

const getStandardPlanColProps = () => ({ xs: 12, sm: 6, md: 4, lg: 4 });

const getEnterprisePlanColProps = () => ({ xs: 12 });

const sortCatalogPlans = (plans) =>
	[...plans].sort((a, b) => getPlanTier(a) - getPlanTier(b));

const getCurrentPlanBillingText = (planInformation) => {
	const date = planInformation?.access_until;
	if (!date || date === 'N/A') return null;
	if (!planInformation?.is_paid_plan && !planInformation?.cancellation_pending) {
		return null;
	}
	if (planInformation.locked) {
		return <>Your subscription ended on {date}. Reactivate to restore access.</>;
	}
	if (planInformation.cancellation_pending && planInformation.access_expired) {
		return <>Your subscription ended on {date}. Reactivate to restore access.</>;
	}
	if (planInformation.pending_plan && planInformation.pending_plan_effective_at) {
		const appliesAt = planInformation.pending_plan_applies_at || planInformation.pending_plan_effective_at;
		return (
			<>
				You keep {planInformation.subscription} through <strong>{planInformation.pending_plan_effective_at}</strong>.
				<br />
				{planInformation.pending_plan} starts <strong>{appliesAt}</strong>.
			</>
		);
	}
	if (planInformation.cancellation_pending) {
		return <>Your plan is active until {date}.<br />No further charges.</>;
	}
	return `Your next charge will be on ${date}.`;
};

const BillingRenewalInfo = ({ isCurrent, planInformation, isBillingSyncing, isOwner }) => {
	if (!isCurrent) {
		return null;
	}
	if (isOwner && isBillingSyncing) {
		return (
			<p className={styles.billingSyncInfo} aria-live="polite">
				<Spinner animation="border" size="sm" role="status" aria-hidden="true" />
				<span>Syncing billing…</span>
			</p>
		);
	}
	const renewalInfo = getCurrentPlanBillingText(planInformation);
	if (!renewalInfo) {
		return null;
	}
	return <p className={styles.nextChargeInfo}>{renewalInfo}</p>;
};

const PlanIcon = ({ planName }) => {
	const Icon = PLAN_ICONS[planName] || BsGift;
	return (
		<div className={styles.catalogCardIcon}>
			<Icon />
		</div>
	);
};

const CatalogFeatureList = ({ plan }) => (
	<ul className={styles.catalogFeatures}>
		{PLAN_FEATURES.map((feature) => {
			const included = feature.type === 'number' || plan[feature.key] === 1;
			const offLimit = plan.off_limits?.indexOf(feature.key) !== -1;
			const text = feature.type === 'number'
				? `${formatLimit(plan[feature.key], feature.suffix ? ` ${feature.suffix}` : '')} ${feature.name}`
				: feature.name;

			return (
				<li key={feature.key}>
					{included
						? <FaCheckCircle className={styles.catalogCheck} />
						: <FaExclamationCircle className={styles.catalogCheckMuted} />}
					<span>{text}</span>
					{offLimit && (
						<Badge bg="warning" text="dark" className={styles.offLimitBadge}>Off limit</Badge>
					)}
				</li>
			);
		})}
	</ul>
);

const SkeletonBone = ({ width = '100%', height = 14, className = '', circle = false }) => (
	<Placeholder as="span" animation="glow" className={`${styles.skelBone} ${className}`} style={{ display: 'block', width }}>
		<span className={`placeholder ${circle ? 'rounded-circle' : 'rounded'}`} style={{ display: 'block', width: '100%', height }} />
	</Placeholder>
);

const CatalogCardSkeleton = () => (
	<Col {...getStandardPlanColProps()} className="mb-3">
		<div className={`${styles.catalogCard} ${styles.skelCard}`}>
			<SkeletonBone width="2.75rem" height={44} circle className={styles.skelIcon} />
			<SkeletonBone width="45%" height={16} className={styles.skelCenter} />
			<SkeletonBone width="60%" height={30} className={`${styles.skelCenter} mt-2`} />
			<SkeletonBone width="80%" height={12} className={`${styles.skelCenter} mt-2 mb-3`} />
			<div className={styles.skelFeatures}>
				{Array.from({ length: 7 }).map((_, i) => (
					<SkeletonBone key={i} width={`${90 - i * 6}%`} height={12} />
				))}
			</div>
			<SkeletonBone width="100%" height={44} className="mt-3" />
		</div>
	</Col>
);

const EnterpriseCardSkeleton = () => (
	<Col xs={12} className="mb-3">
		<div className={`${styles.catalogCard} ${styles.enterpriseCard} ${styles.skelCard}`}>
			<div className={styles.enterpriseCardLayout}>
				<div className={styles.enterpriseIntro}>
					<SkeletonBone width="2.75rem" height={44} circle className={styles.skelIcon} />
					<SkeletonBone width="55%" height={16} className={`${styles.skelCenter} mt-2`} />
					<SkeletonBone width="45%" height={26} className={`${styles.skelCenter} mt-2`} />
					<SkeletonBone width="90%" height={12} className={`${styles.skelCenter} mt-2`} />
					<SkeletonBone width="80%" height={12} className={`${styles.skelCenter} mt-1 mb-3`} />
					<SkeletonBone width="100%" height={44} />
				</div>
				<div className={styles.enterpriseColumns}>
					{[0, 1].map((col) => (
						<div key={col} className={styles.enterpriseColumn}>
							<SkeletonBone width="50%" height={12} className="mb-2" />
							<SkeletonBone width="90%" height={11} className="mb-3" />
							<div className={styles.skelFeatures}>
								{Array.from({ length: 6 }).map((_, i) => (
									<SkeletonBone key={i} width={`${88 - i * 7}%`} height={12} />
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	</Col>
);

const isActiveIpaymerSubscription = (sub) => {
	if (!sub) return false;
	if (sub.active === true) return true;
	const status = String(sub.status || '').toLowerCase();
	return status === 'active' || status === 'paid';
};

const pickActiveSubscription = (subs) => {
	if (!Array.isArray(subs)) return null;
	return subs.find(isActiveIpaymerSubscription) || null;
};

const OWNER_ONLY_TOOLTIP = 'Only the workspace owner can manage subscriptions';

const Plan = () => {
	const user = useSelector((state) => state?.userReducer?.user);
	const isOwner = Boolean(user?.isOwner);
	const [planInformation, setPlanInformation] = useState({});
	const [subscriptions, setSubscriptions] = useState([]);
	const [errorMessage, setErrorMessage] = useState('');
	const [successMessage, setSuccessMessage] = useState('');
	const [isPageLoading, setIsPageLoading] = useState(true);
	const [isBillingSyncing, setIsBillingSyncing] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isPaymentMethodsLoading, setIsPaymentMethodsLoading] = useState(false);

	const [showSelectPaymentModal, setShowSelectPaymentModal] = useState(false);
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [successModal, setSuccessModal] = useState(null);

	const [cancelStep, setCancelStep] = useState(null); // 'offer' | 'reason' | 'confirm'
	const [cancelTarget, setCancelTarget] = useState(null);
	const [cancelReason, setCancelReason] = useState('');
	const [cancelReasonDetails, setCancelReasonDetails] = useState('');
	const [newCard, setNewCard] = useState({});
	const [selectedCard, setSelectedCard] = useState({});
	const [paymentSelectionError, setPaymentSelectionError] = useState('');
	const [selectedPlan, setSelectedPlan] = useState('');
	const [selectedSubscription, setSelectedSubscription] = useState(false);
	const [plansTab, setPlansTab] = useState('standard');
	const [planQuote, setPlanQuote] = useState(null);
	const [isQuoteLoading, setIsQuoteLoading] = useState(false);
	const [quoteError, setQuoteError] = useState('');

	const currentPlan = planInformation.update?.find((p) => p.name === planInformation.subscription);
	const activeSubscription = isBillingSyncing
		? null
		: (pickActiveSubscription(subscriptions)
			|| (planInformation.subscription_id && planInformation.is_paid_plan
				? { id: planInformation.subscription_id, status: 'active', active: true }
				: null));
	const showBillingAlerts = !isOwner || !isBillingSyncing;
	const catalogPlans = sortCatalogPlans(
		(planInformation.update || []).filter((p) => !isAdditionalUserPlan(p))
	);
	const standardPlans = catalogPlans.filter((p) => !isEnterprisePlan(p));
	const enterprisePlan = catalogPlans.find(isEnterprisePlan);

	const downgradeOptions = currentPlan
		? standardPlans.filter((p) =>
			p.plan_code &&
			p.name !== currentPlan.name &&
			getPlanTier(p) < getPlanTier(currentPlan) &&
			p.can_update
		)
		: [];
	const cancelAccessUntil = cancelTarget
		? (cancelTarget.next_billing_on || cancelTarget.next_billing_date || planInformation.access_until)
		: null;

	useEffect(() => {
		getPlanInformation();
	}, []);

	useEffect(() => {
		if (planInformation.subscription === 'Enterprise') {
			setPlansTab('enterprise');
		}
	}, [planInformation.subscription]);

	const getPlanInformation = async ({ silent = false, includeLive = true } = {}) => {
		if (!silent) {
			setIsPageLoading(true);
		}
		try {
			const response = await axios.get(`${baseURL}subscription`, headers);
			setPlanInformation(response.data['plan_information']);
			setSubscriptions([]);
			setErrorMessage('');
		} catch (error) {
			console.error(error);
			if (!silent) {
				setErrorMessage('Something went wrong. Please try again later.');
			}
		} finally {
			if (!silent) {
				setIsPageLoading(false);
			}
		}

		if (includeLive && isOwner) {
			fetchLiveSubscription({ silent: true });
		}
	};

	const fetchLiveSubscription = async ({ silent = false } = {}) => {
		setIsBillingSyncing(true);
		try {
			const response = await axios.get(`${baseURL}subscription/live`, headers);
			if (Array.isArray(response.data?.subscriptions)) {
				setSubscriptions(response.data.subscriptions);
			}
			if (response.data?.plan_information) {
				setPlanInformation((prev) => ({
					...prev,
					...response.data.plan_information,
				}));
			}
		} catch (error) {
			if (!silent) {
				console.error(error);
			}
		} finally {
			setIsBillingSyncing(false);
		}
	};

	const handleCardCreated = (card) => {
		setShowPaymentModal(false);
		setNewCard(card);
	};

	const fetchPlanQuote = async (planCode, subscription) => {
		if (!isOwner) {
			return;
		}
		setIsQuoteLoading(true);
		setPlanQuote(null);
		setQuoteError('');
		const params = { plan: planCode };
		if (subscription?.id) {
			params.subscription_id = subscription.id;
		}
		try {
			const response = await axios.get(`${baseURL}subscription/quote`, { ...headers, params });
			setPlanQuote(response.data);
		} catch (error) {
			const message = error.response?.data?.message || 'Could not load billing quote.';
			setQuoteError(message);
		} finally {
			setIsQuoteLoading(false);
		}
	};

	const handleSelectPlan = (planCode) => {
		if (!isOwner) {
			return;
		}
		const subscription = planInformation.is_paid_plan ? (activeSubscription || false) : false;
		setSelectedPlan(planCode);
		setSelectedSubscription(subscription);
		setSelectedCard({});
		setPaymentSelectionError('');
		setShowSelectPaymentModal(true);
		fetchPlanQuote(planCode, subscription);
	};

	const confirmSelectedCard = () => {
		if (!isOwner) {
			return;
		}
		if (!selectedCard?.id) {
			setPaymentSelectionError('Please add and select a payment method to continue.');
			return;
		}
		setPaymentSelectionError('');
		setIsSubmitting(true);
		setErrorMessage('');
		setSuccessMessage('');
		let request_data = { plan: selectedPlan, card: selectedCard };
		if (selectedSubscription) {
			request_data = { ...request_data, subscription: selectedSubscription };
		}
		const targetPlan = (planInformation.update || []).find((p) => p.plan_code === selectedPlan);
		const actionWord = targetPlan && currentPlan
			? (getPlanTier(targetPlan) < getPlanTier(currentPlan) ? 'downgraded' : 'upgraded')
			: 'updated';
		axios.patch(`${baseURL}subscription`, request_data, headers)
			.then(async (response) => {
				setShowSelectPaymentModal(false);
				const effectiveDate = response.data?.pending_plan_effective_at;
				setSuccessModal({
					title: actionWord === 'downgraded' ? 'Downgrade scheduled' : `Subscription ${actionWord}`,
					message: actionWord === 'downgraded' && effectiveDate
						? `Your downgrade is scheduled. You keep your current plan until ${effectiveDate}, then billing switches to the new plan. No charge today.`
						: 'You have successfully updated your subscription.',
				});
				await getPlanInformation({ silent: true });
			})
			.catch(error => {
				if (error.response?.data?.message) {
					setPaymentSelectionError(error.response.data.message);
				} else {
					console.error(error);
					setPaymentSelectionError('Something went wrong. Please try again later.');
				}
			}).finally(() => setIsSubmitting(false));
	};

	const openCancelFlow = (subscription) => {
		if (!isOwner) {
			return;
		}
		setCancelTarget(subscription);
		setCancelReason('');
		setCancelReasonDetails('');
		setErrorMessage('');
		setSuccessMessage('');
		setCancelStep('offer');
	};

	const closeCancelFlow = () => {
		setCancelStep(null);
		setCancelTarget(null);
	};

	const handleDowngradeInstead = (planCode) => {
		closeCancelFlow();
		handleSelectPlan(planCode);
	};

	const submitCancellation = () => {
		if (!cancelTarget) return;
		setCancelStep(null);
		setIsSubmitting(true);
		setErrorMessage('');
		setSuccessMessage('');
		const details = cancelReasonDetails.trim();
		axios.delete(`${baseURL}subscription/${cancelTarget.id}`, {
			...headers,
			data: { reason: cancelReason, reason_details: details || null },
		})
			.then(async (response) => {
				const pending = response.data?.cancellation_pending;
				const accessUntil = response.data?.access_until;
				setSuccessModal({
					title: 'Subscription cancelled',
					message: pending && accessUntil
						? `Your subscription has been cancelled. You'll keep full access until ${accessUntil}, after which your workspace will be locked.`
						: 'Your subscription has been cancelled.',
				});
				setCancelTarget(null);
				await getPlanInformation({ silent: true });
			})
			.catch(error => {
				if (error.response?.data?.message) {
					setErrorMessage(error.response.data.message);
				} else {
					console.error(error);
					setErrorMessage('Something went wrong. Please try again later.');
				}
			}).finally(() => setIsSubmitting(false));
	};

	const renderEnterpriseCard = (plan, isCurrent) => {
		return (
		<div className={`${styles.catalogCard} ${styles.enterpriseCard}`}>
			{isCurrent && <span className={styles.catalogActiveBadge}>Active</span>}
			<div className={styles.enterpriseCardLayout}>
				<div className={styles.enterpriseIntro}>
					<PlanIcon planName={plan.name} />
					<h4 className={styles.catalogCardName}>{plan.name}</h4>
					<div className={styles.catalogCardPrice}>Let&apos;s talk</div>
					<BillingRenewalInfo
						isCurrent={isCurrent}
						planInformation={planInformation}
						isBillingSyncing={isBillingSyncing}
						isOwner={isOwner}
					/>
					<p className={styles.catalogCardDesc}>
						{plan.description || ENTERPRISE_CONTENT.tagline}
					</p>
					<p className={styles.enterpriseNote}>
						{plan.description ? (
							<>{ENTERPRISE_CONTENT.tagline} — {ENTERPRISE_CONTENT.description}</>
						) : (
							ENTERPRISE_CONTENT.description
						)}
					</p>
					<div className={styles.enterpriseActions}>
						{isCurrent ? (
							<Button variant="outline-secondary" size="lg" disabled className={`w-100 ${styles.catalogBtn}`}>
								Current Plan
							</Button>
						) : (
							<Button variant="primary" href={ENTERPRISE_CONTENT.contactUrl} size="lg" className={`w-100 ${styles.catalogBtn}`}>
								Contact Us
							</Button>
						)}
						<p className={styles.enterpriseContactNote}>
							Or email{' '}
							<a href={`mailto:${ENTERPRISE_CONTENT.contactEmail}`}>{ENTERPRISE_CONTENT.contactEmail}</a>
						</p>
						{isCurrent && isOwner && planInformation.can_cancel && activeSubscription && !planInformation.cancellation_pending && (
							<Button variant="link" size="sm" className={styles.cancelLink} onClick={() => openCancelFlow(activeSubscription)}>
								Cancel subscription
							</Button>
						)}
					</div>
				</div>
				<div className={styles.enterpriseColumns}>
					<div className={styles.enterpriseColumn}>
						<p className={styles.enterpriseSectionTitle}>What&apos;s included</p>
						<p className={styles.enterpriseCapacityNote}>{ENTERPRISE_CONTENT.offeringsNote}</p>
						<ul className={styles.catalogFeatures}>
							{ENTERPRISE_CONTENT.offerings.map((item) => (
								<li key={item}>
									<FaCheckCircle className={styles.catalogCheck} />
									<span>{item}</span>
								</li>
							))}
						</ul>
					</div>
					<div className={styles.enterpriseColumn}>
						<p className={styles.enterpriseSectionTitle}>Baseline capacity</p>
						<p className={styles.enterpriseCapacityNote}>{ENTERPRISE_CONTENT.capacityNote}</p>
						<CatalogFeatureList plan={plan} />
					</div>
				</div>
			</div>
		</div>
		);
	};

	const renderCatalogPlanCard = (plan) => {
		const needsReactivation = Boolean(planInformation.locked || planInformation.access_expired);
		const actionLabel = getPlanActionLabel(currentPlan, plan, { needsReactivation });
		const isCurrent = plan.name === planInformation.subscription;
		const isPendingTarget = planInformation.pending_plan === plan.name;
		const isDowngradeAction = getPlanTier(plan) < getPlanTier(currentPlan);
		const hasPendingChange = Boolean(planInformation.pending_plan);
		const isPlanChangeBlocked = isPendingTarget || (hasPendingChange && isDowngradeAction);
		const canReactivateCurrent = needsReactivation && isCurrent && !!plan.plan_code;

		const buttonLabel = canReactivateCurrent
			? 'Reactivate'
			: isCurrent
				? 'Current Plan'
				: isPendingTarget
					? 'Scheduled'
					: !plan.can_update
						? 'Usage Exceeded'
						: hasPendingChange && isDowngradeAction
							? 'Change scheduled'
							: actionLabel;

		const canChangePlan = isOwner && !isBillingSyncing && plan.can_update && !!plan.plan_code && !isPlanChangeBlocked
			&& (!isCurrent || canReactivateCurrent);
		const planButtonTitle = !isOwner
			? OWNER_ONLY_TOOLTIP
			: isBillingSyncing
				? 'Syncing billing details…'
			: canReactivateCurrent
				? 'Pay to restore access for this plan'
			: isPendingTarget
				? `Starts ${planInformation.pending_plan_applies_at || planInformation.pending_plan_effective_at || 'soon'}`
				: hasPendingChange && isDowngradeAction
					? 'You already have a plan change scheduled'
					: !plan.plan_code && !isCurrent
						? 'Cancel your subscription to move to the Free plan'
						: undefined;

		return (
			<Col key={plan.name} {...getStandardPlanColProps()} className="mb-3">
				<div className={styles.catalogCard}>
					{isCurrent && (
						<span className={styles.catalogActiveBadge}>
							{needsReactivation ? 'Expired' : 'Active'}
						</span>
					)}
					{isPendingTarget && !isCurrent && (
						<span className={styles.catalogActiveBadge}>Scheduled</span>
					)}
					<PlanIcon planName={plan.name} />
					<h4 className={styles.catalogCardName}>{plan.name}</h4>
					<div className={styles.catalogCardPrice}>
						{plan.amount > 0 ? (
							<>${plan.amount}<small>/month</small></>
						) : (
							'Free'
						)}
					</div>
					<BillingRenewalInfo
						isCurrent={isCurrent}
						planInformation={planInformation}
						isBillingSyncing={isBillingSyncing}
						isOwner={isOwner}
					/>
					{plan.description && (
						<p className={styles.catalogCardDesc}>{plan.description}</p>
					)}
					<CatalogFeatureList plan={plan} />
					<Button
						disabled={!canChangePlan}
						variant={canChangePlan ? 'primary' : 'outline-secondary'}
						size="lg"
						className={`w-100 ${styles.catalogBtn} ${!isOwner && !isCurrent && plan.plan_code ? styles.catalogBtnOwnerDisabled : ''}`}
						onClick={() => canChangePlan && handleSelectPlan(plan.plan_code)}
						title={planButtonTitle}
					>
						{buttonLabel}
					</Button>
					{isCurrent && isOwner && planInformation.can_cancel && activeSubscription && !planInformation.cancellation_pending && !needsReactivation && (
						<Button variant="link" size="sm" className={styles.cancelLink} onClick={() => openCancelFlow(activeSubscription)}>
							Cancel subscription
						</Button>
					)}
				</div>
			</Col>
		);
	};

	return (
		<MainContainer>
			<Container fluid="sm" className={styles.page}>
				{errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
				{successMessage && <Alert variant="success">{successMessage}</Alert>}
				{showBillingAlerts && planInformation.locked ? (
					<Alert variant="danger">
						Your workspace is locked because your subscription ended. Choose a plan below (or reactivate Standard) to restore access.
					</Alert>
				) : showBillingAlerts && planInformation.cancellation_pending && planInformation.access_until && (
					<Alert variant="warning">
						Your subscription is cancelled and will stay active until <strong>{planInformation.access_until}</strong>. After that your workspace will be locked until you choose a new plan.
					</Alert>
				)}
				{showBillingAlerts && !planInformation.locked && planInformation.pending_plan && planInformation.pending_plan_effective_at && (
					<Alert variant="info">
						You keep your current plan through <strong>{planInformation.pending_plan_effective_at}</strong>.
						{' '}<strong>{planInformation.pending_plan}</strong> starts{' '}
						<strong>{planInformation.pending_plan_applies_at || planInformation.pending_plan_effective_at}</strong>.
					</Alert>
				)}

				{/* Plans catalog */}
				{!errorMessage && (
					<>
						<div className={styles.plansCatalogHeader}>
							<h5 className={styles.plansCatalogTitle}>Choose the right plan for you</h5>
							<p className={styles.plansCatalogSubtitle}>There&apos;s a plan for every team.</p>
							{isOwner && isBillingSyncing && (
								<p className={styles.billingSyncBanner} aria-live="polite">
									<Spinner animation="border" size="sm" role="status" aria-hidden="true" />
									<span>Syncing billing…</span>
								</p>
							)}
						</div>

						<div className={styles.plansTabBar}>
							<div className={styles.plansTabSwitch} role="tablist">
								<button
									type="button"
									role="tab"
									aria-selected={plansTab === 'standard'}
									className={plansTab === 'standard' ? styles.plansTabActive : styles.plansTab}
									onClick={() => setPlansTab('standard')}
								>
									Free, Standard &amp; Premium
								</button>
								<button
									type="button"
									role="tab"
									aria-selected={plansTab === 'enterprise'}
									className={plansTab === 'enterprise' ? styles.plansTabActive : styles.plansTab}
									onClick={() => setPlansTab('enterprise')}
								>
									Enterprise
								</button>
							</div>
							{!isOwner && (
								<OverlayTrigger
									placement="top"
									overlay={<Tooltip id="owner-only-plans-tooltip">{OWNER_ONLY_TOOLTIP}</Tooltip>}
								>
									<span className={styles.ownerOnlyHint} role="img" aria-label={OWNER_ONLY_TOOLTIP}>
										<FaInfoCircle />
									</span>
								</OverlayTrigger>
							)}
						</div>

						{isPageLoading ? (
							<Row className={styles.catalogPlansRow}>
								{plansTab === 'enterprise' ? (
									<EnterpriseCardSkeleton />
								) : (
									Array.from({ length: 3 }).map((_, i) => <CatalogCardSkeleton key={i} />)
								)}
							</Row>
						) : plansTab === 'standard' ? (
							standardPlans.length > 0 ? (
								<Row className={styles.catalogPlansRow}>
									{standardPlans.map(renderCatalogPlanCard)}
								</Row>
							) : (
								<p className="text-muted text-center py-4">No plans available.</p>
							)
						) : (
							enterprisePlan ? (
								<Row className={styles.catalogPlansRow}>
									<Col {...getEnterprisePlanColProps()} className="mb-3">
										{renderEnterpriseCard(
											enterprisePlan,
											enterprisePlan.name === planInformation.subscription
										)}
									</Col>
								</Row>
							) : (
								<p className="text-muted text-center py-4">Enterprise plan is not available.</p>
							)
						)}
					</>
				)}
			</Container>

			<Modal fullscreen="lg-down" className="plans-modal" size="xl" show={showSelectPaymentModal} onHide={() => setShowSelectPaymentModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Select Payment Method</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{paymentSelectionError && <Alert variant="danger">{paymentSelectionError}</Alert>}
					{(isQuoteLoading || planQuote || quoteError) && (
						<div className={styles.quotePanel}>
							{isQuoteLoading && (
								<div className={styles.quoteLoading}>
									<Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
									<span>Loading billing quote…</span>
								</div>
							)}
							{quoteError && !isQuoteLoading && (
								<Alert variant="warning" className="mb-0">{quoteError}</Alert>
							)}
							{planQuote && !isQuoteLoading && (
								<div className={styles.quoteSummary}>
									<div className={styles.quoteHeading}>
										{planQuote.event === 'Downgrade' ? 'Downgrade' : planQuote.event === 'Upgrade' ? 'Upgrade' : planQuote.event === 'Reactivation' ? 'Reactivation' : planQuote.change_type === 'new' ? 'New subscription' : 'Plan change'}
										{' — '}{planQuote.plan_name}
									</div>
									<div className={styles.quoteRow}>
										<span>Recurring price</span>
										<strong>{planQuote.plan_price}</strong>
									</div>
									{planQuote.event === 'Downgrade' ? (
										<>
											<div className={styles.quoteRow}>
												<span>Amount due today</span>
												<strong className={styles.quoteAmount}>$0.00</strong>
											</div>
											<div className={styles.quoteRow}>
												<span>New plan starts</span>
												<strong>{planQuote.effective_date}</strong>
											</div>
										</>
									) : (
										<>
											<div className={styles.quoteRow}>
												<span>Amount due today</span>
												<strong className={styles.quoteAmount}>
													{planQuote.amount_due_today
														?? (planQuote.event === 'Upgrade' ? 'Unavailable' : '—')}
												</strong>
											</div>
											{planQuote.event === 'Upgrade' && !planQuote.amount_due_today && (
												<div className={styles.quoteRow}>
													<span className="text-danger" style={{ fontSize: '0.875rem' }}>
														Couldn't calculate your upgrade price right now.{' '}
														<Button
															variant="link"
															size="sm"
															className="p-0 align-baseline"
															disabled={isQuoteLoading}
															onClick={() => fetchPlanQuote(selectedPlan, selectedSubscription)}
														>
															Try again
														</Button>
													</span>
												</div>
											)}
											{planQuote.next_renewal_date && (
												<div className={styles.quoteRow}>
													<span>Renews on</span>
													<strong>{planQuote.next_renewal_date}</strong>
												</div>
											)}
										</>
									)}
								</div>
							)}
						</div>
					)}
					<Cards
						viewTitle="Select Payment Method"
						viewMode="select"
						onSelect={(card) => {
							setSelectedCard(card);
							setPaymentSelectionError('');
						}}
						showCardModal={() => setShowPaymentModal(true)}
						newCard={newCard}
						onLoadingChange={setIsPaymentMethodsLoading}
					/>
					<NewCard showPaymentModal={showPaymentModal} onCreate={handleCardCreated} hidePaymentModal={() => setShowPaymentModal(false)} />
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => {
						setShowSelectPaymentModal(false);
						setPlanQuote(null);
						setQuoteError('');
					}} disabled={isSubmitting || isPaymentMethodsLoading}>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={confirmSelectedCard}
						disabled={
							!isOwner
							|| isSubmitting
							|| isPaymentMethodsLoading
							|| isQuoteLoading
							|| !selectedCard?.id
							|| (planQuote?.event === 'Upgrade' && !planQuote?.amount_due_today)
						}
					>
						{isSubmitting ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Confirm'}
					</Button>
				</Modal.Footer>
			</Modal>

			<Modal
				show={!!successModal}
				onHide={() => setSuccessModal(null)}
				centered
				dialogClassName={styles.successModal}
				contentClassName={styles.successModalContent}
				backdropClassName={styles.successBackdrop}
			>
				<Modal.Body className={styles.successBody}>
					<div className={styles.successCheck} aria-hidden="true">
						<svg viewBox="0 0 52 52" className={styles.successCheckSvg}>
							<circle className={styles.successCheckCircle} cx="26" cy="26" r="24" fill="none" />
							<path className={styles.successCheckMark} fill="none" d="M14 27 l8 8 l16 -18" />
						</svg>
					</div>
					<h3 className={styles.successTitle}>{successModal?.title}</h3>
					<p className={styles.successText}>{successModal?.message}</p>
					<Button variant="primary" className={styles.successBtn} onClick={() => setSuccessModal(null)}>
						Done
					</Button>
				</Modal.Body>
			</Modal>

			<Modal
				show={cancelStep !== null}
				onHide={closeCancelFlow}
				centered
				dialogClassName={styles.cancelModal}
				contentClassName={styles.cancelModalContent}
			>
				{cancelStep === 'offer' && (
					<Modal.Body className={styles.cancelBody}>
						<h3 className={styles.cancelTitle}>Before you go</h3>
						<p className={styles.cancelText}>
							{downgradeOptions.length > 0
								? 'You can switch to a lower-priced plan instead of cancelling and keep your workspace active.'
								: 'Cancelling will stop your subscription. You can always choose a plan again later.'}
						</p>
						{downgradeOptions.length > 0 && (
							<div className={styles.cancelPlanList}>
								{downgradeOptions.map((p) => (
									<button
										type="button"
										key={p.name}
										className={styles.cancelPlanOption}
										onClick={() => handleDowngradeInstead(p.plan_code)}
									>
										<span className={styles.cancelPlanName}>{p.name}</span>
										<span className={styles.cancelPlanPrice}>${p.amount}<small>/mo</small></span>
									</button>
								))}
							</div>
						)}
						<div className={styles.cancelActions}>
							<Button variant="primary" className={styles.cancelKeepBtn} onClick={closeCancelFlow}>
								Keep my plan
							</Button>
							<button type="button" className={styles.cancelProceedLink} onClick={() => setCancelStep('reason')}>
								No, I want to cancel
							</button>
						</div>
					</Modal.Body>
				)}

				{cancelStep === 'reason' && (
					<Modal.Body className={styles.cancelBody}>
						<h3 className={styles.cancelTitle}>Why are you cancelling?</h3>
						<p className={styles.cancelText}>Your feedback helps us improve.</p>
						<div className={styles.cancelReasons}>
							{CANCELLATION_REASONS.map((reason) => (
								<label
									key={reason}
									className={`${styles.cancelReasonRow} ${cancelReason === reason ? styles.cancelReasonActive : ''}`}
								>
									<input
										type="radio"
										name="cancelReason"
										value={reason}
										checked={cancelReason === reason}
										onChange={() => setCancelReason(reason)}
									/>
									<span>{reason}</span>
								</label>
							))}
						</div>
						<textarea
							className={styles.cancelTextarea}
							rows={3}
							placeholder={cancelReason === 'Other' ? 'Please tell us more (required)' : 'Anything else you would like to share? (optional)'}
							value={cancelReasonDetails}
							onChange={(e) => setCancelReasonDetails(e.target.value)}
						/>
						<div className={styles.cancelActions}>
							<Button variant="light" className={styles.cancelBackBtn} onClick={() => setCancelStep('offer')}>
								Back
							</Button>
							<Button
								variant="danger"
								className={styles.cancelProceedBtn}
								disabled={!cancelReason || (cancelReason === 'Other' && !cancelReasonDetails.trim())}
								onClick={() => setCancelStep('confirm')}
							>
								Continue
							</Button>
						</div>
					</Modal.Body>
				)}

				{cancelStep === 'confirm' && (
					<Modal.Body className={styles.cancelBody}>
						<h3 className={styles.cancelTitle}>Confirm cancellation</h3>
						<p className={styles.cancelText}>
							{cancelAccessUntil && cancelAccessUntil !== 'N/A'
								? <>You&apos;ll keep full access until <strong>{cancelAccessUntil}</strong>. After that, your workspace will be locked until you choose a new plan.</>
								: 'Your workspace will be locked until you choose a new plan.'}
						</p>
						<div className={styles.cancelActions}>
							<Button variant="light" className={styles.cancelBackBtn} onClick={() => setCancelStep('reason')}>
								Back
							</Button>
							<Button variant="danger" className={styles.cancelProceedBtn} onClick={submitCancellation}>
								Cancel subscription
							</Button>
						</div>
					</Modal.Body>
				)}
			</Modal>
		</MainContainer>
	);
};

export default Plan;
